import React, { useState, useEffect } from "react";
import { find } from "lodash";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema, FormData } from "./validation";
import { PatternFormat } from "react-number-format";
import { IconArrowUpRight, IconCircle, IconCircleCheckFilled, IconSquare, IconSquareCheckFilled, IconChevronDown } from "@tabler/icons-react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import Button from "../../../../components/ui/Button";
import useCallApiAndLoad from "../../../../hooks/useCallApiAndLoad";
import { fetchResponseAdapter } from "../../../../adapters/fetchAdapter";
import { getCodesCountries } from "../../../../services/publicService";

interface PhoneData {
  name: string;
  code: string;
  flag: string;
  phone_length: number;
  phone_area_code: string;
  phone_mask: string;
}

// Mapeo de código de país interno → phone_area_code para seleccionar el default
const COUNTRY_TO_AREA: Record<string, string> = {
  sv: "+503",
  gt: "+502",
  pa: "+507",
};

const INTEREST_OPTIONS = [
  "Soy Propietario",
  "Estoy buscando comprar",
  "Solo quiero análisis de mercado",
];

interface Props {
  country: string;
  countryName: string;
  onClose: () => void;
}

const NewsletterForm: React.FC<Props> = ({ country, countryName, onClose }) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      interest: INTEREST_OPTIONS[0],
      terms: false,
    },
  });

  const interest = watch("interest");
  const terms = watch("terms");

  // Phone selector state (replica de PhoneSelector)
  const { callEndpoint } = useCallApiAndLoad();
  const [countries, setCountries] = useState<PhoneData[]>([]);
  const [selected, setSelected] = useState<PhoneData | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const load = async () => {
      const { status, data } = await callEndpoint(getCodesCountries());
      if (status !== 200) return;
      const list = fetchResponseAdapter(data).data as PhoneData[];
      setCountries(list);
      // Seleccionar el país del mapa como default
      const defaultArea = COUNTRY_TO_AREA[country] || "+503";
      const match = find(list, (c) => c.phone_area_code === defaultArea);
      setSelected(match || list[0]);
    };
    load().catch(console.error);
  }, [country]);

  const filteredCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone_area_code.includes(searchTerm)
  );

  const onSubmit = (data: FormData) => {
    // TODO: integrar con endpoint de newsletter
    console.log(data);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-green600 p-6 md:p-8 flex flex-col min-h-full overflow-y-auto max-h-[calc(100dvh-2rem)]">
      <h2 className="text-base md:text-base font-semibold text-white text-center leading-tight mb-6 leading-[1.1]">
        Accede al Reporte<br />del Mercado Inmobiliario
      </h2>

      {/* Nombre / Apellido */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-sm font-medium text-white mb-1 block">Nombre</label>
          <input
            type="text"
            {...register("firstName")}
            className="w-full bg-transparent ring-1 ring-[#00854E] rounded-lg px-3 h-[30px] text-sm text-[#005230] outline-none focus:ring-2 focus:ring-white focus:text-white"
          />
          {errors.firstName && <span className="text-[10px] font-light text-[#FF7878]">{errors.firstName.message}</span>}
        </div>
        <div>
          <label className="text-sm font-medium text-white mb-1 block">Apellido</label>
          <input
            type="text"
            {...register("lastName")}
            className="w-full bg-transparent ring-1 ring-[#00854E] rounded-lg px-3 h-[30px] text-sm text-[#005230] outline-none focus:ring-2 focus:ring-white focus:text-white"
          />
          {errors.lastName && <span className="text-[10px] font-light text-[#FF7878]">{errors.lastName.message}</span>}
        </div>
      </div>

      {/* Correo */}
      <div className="mb-3">
        <label className="text-sm font-medium text-white mb-1 block">Correo electrónico</label>
        <input
          type="email"
          {...register("email")}
          className="w-full bg-transparent ring-1 ring-[#00854E] rounded-lg px-3 h-[30px] text-sm text-[#005230] outline-none focus:ring-2 focus:ring-white focus:text-white"
        />
        {errors.email && <span className="text-[10px] font-light text-[#FF7878]">{errors.email.message}</span>}
      </div>

      {/* Teléfono — replica funcionalidad de PhoneSelector con estilos del modal */}
      <div className="mb-4">
        <label className="text-sm font-medium text-white mb-1 block">Número de teléfono</label>
        <div className="flex h-[30px] ring-1 ring-[#00854E] rounded-lg focus-within:ring-2 focus-within:ring-white">
          {/* Country selector con Listbox */}
          <Listbox value={selected} onChange={(v) => { setSelected(v); setSearchTerm(""); }}>
            <div className="dynamic relative">
              <ListboxButton className="flex items-center gap-2 h-full bg-transparent px-3 text-sm text-white focus:outline-none cursor-pointer">
                {selected?.flag && (
                  <img src={selected.flag} alt="" className="h-5 w-5 rounded-full object-cover object-center" />
                )}
                <span className="truncate">{selected?.phone_area_code}</span>
                <IconChevronDown className="h-4 w-4 text-white/60" />
              </ListboxButton>

              <ListboxOptions className="absolute z-20 mt-1 max-h-60 w-[220px] overflow-auto bg-white text-sm shadow-md border border-gray-200 rounded-md">
                <div className="sticky top-0 bg-white p-2 z-10">
                  <input
                    type="text"
                    placeholder="Buscar país o código"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border px-2 py-1 text-sm rounded focus:outline-none"
                  />
                </div>

                {filteredCountries.length === 0 && (
                  <div className="px-3 py-2 text-gray-500">No se encontraron resultados</div>
                )}

                {filteredCountries.map((c, idx) => (
                  <ListboxOption
                    key={idx}
                    value={c}
                    className={({ focus }) =>
                      `flex items-center gap-2 px-3 py-2 cursor-pointer ${focus ? "bg-blue-100" : ""}`
                    }
                  >
                    <img src={c.flag} alt="" className="h-5 w-5 rounded-full object-cover object-center" />
                    <span className="text-sm truncate">{c.name}</span>
                    <span className="ml-auto text-[#757575]">{c.phone_area_code}</span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>

          {/* Phone input con máscara dinámica */}
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PatternFormat
                format={selected?.phone_mask || "###"}
                allowEmptyFormatting
                mask="_"
                className="flex-1 bg-transparent px-3 h-[30px] text-sm text-[#005230] outline-none focus:text-white"
                value={field.value}
                onValueChange={(values) => field.onChange(values.value)}
              />
            )}
          />
        </div>
        {errors.phone && <span className="text-[10px] font-light text-[#FF7878]">{errors.phone.message}</span>}
      </div>

      {/* Interés */}
      <div className="mb-4">
        <label className="text-sm font-medium text-white mb-2 block">Interés principal</label>
        <Controller
          name="interest"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              {INTEREST_OPTIONS.map((option) => (
                <label
                  key={option}
                  className={`group flex items-center gap-3 rounded-lg px-3 h-[30px] text-sm cursor-pointer transition-colors ${
                    field.value === option
                      ? "bg-transparent ring-2 ring-white text-white"
                      : "bg-transparent ring-1 ring-[#00854E] text-[#005230] hover:text-white hover:ring-white"
                  }`}
                >
                  {field.value === option ? (
                    <IconCircleCheckFilled size={20} className="shrink-0 text-white" />
                  ) : (
                    <IconCircle size={20} className="shrink-0 text-[#005230] group-hover:text-white transition-colors" />
                  )}
                  {option}
                  <input
                    type="radio"
                    name="interest"
                    value={option}
                    checked={field.value === option}
                    onChange={() => field.onChange(option)}
                    className="sr-only"
                  />
                </label>
              ))}
            </div>
          )}
        />
      </div>

      {/* Términos */}
      <Controller
        name="terms"
        control={control}
        render={({ field }) => (
          <label className="group flex items-start gap-2 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              className="sr-only"
            />
            {field.value ? (
              <IconSquareCheckFilled size={13} className="shrink-0 text-white" />
            ) : (
              <IconSquare size={13} className="shrink-0 text-[#005230] group-hover:text-white transition-colors" />
            )}
            <span className={`text-[10px] transition-colors ${field.value ? "text-white" : "text-[#005230] group-hover:text-white"}`}>
              He leído y acepto los <a href="#" target="_blank" rel="noopener noreferrer" className="underline font-medium text-white">términos y condiciones</a>
            </span>
          </label>
        )}
      />

      {/* Submit */}
      <Button
        type="submit"
        disabled={!terms}
        variant="white"
        size="base"
        className="!w-full !rounded-xl !h-[42px] !text-[14px] !font-semibold leading-none !tracking-tight"
        endIcon={<IconArrowUpRight size={18} />}
      >
        Recibir análisis
      </Button>
    </form>
  );
};

export default NewsletterForm;
